import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import IndicatorForecast from '../../../ReportBuilder/Components/IndicatorDetails/IndicatorDetailQuestions/IndicatorForecast';
import IndicatorFileUpload from '../../../ReportBuilder/Components/IndicatorDetails/IndicatorDetailQuestions/IndicatorFileUpload';
import IndicatorMeasurement from '../../../ReportBuilder/Components/IndicatorDetails/IndicatorDetailQuestions/IndicatorMeasurement';
import {
  Indicator,
  IndicatorSectionKey,
} from '../../../ReportBuilder/Components/ImpactIndicator/utils';
import { IndicatorDetails } from '../../../ReportBuilder/Components/IndicatorDetails/utils';
import { ButtonProps } from 'shared/design-system/components/TrueImpactSimpleModal';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { IndicatorDetailsHandler } from './utils';
import { useSaveReportIndicators } from 'shared/providers/IndicatorProvider/hooks';
import { requestInit } from 'shared/auth/hooks/useRequestState';
import { ReportPeriod } from '../../../ReportBuilder/Components/SectionPeriod/utils';

interface ImpactDetailsEditComponentProps {
  questionRef: React.RefObject<IndicatorDetailsHandler>;
  reportId: number;
  sectionKey: IndicatorSectionKey;
  item: IndicatorDetails;
  itemKey: keyof IndicatorDetails;
}

interface ImpactDetailsEditProps
  extends Omit<ImpactDetailsEditComponentProps, 'questionRef'> {
  open: boolean;
  handleCloseModal: () => unknown;
  handleUpdate: (
    sectionKey: IndicatorSectionKey,
    item: IndicatorDetails,
    itemKey: keyof IndicatorDetails,
    answer: IndicatorDetails[keyof IndicatorDetails],
  ) => unknown;
  handleVerified: (
    sectionKey: IndicatorSectionKey,
    item: IndicatorDetails,
    itemKey: keyof IndicatorDetails,
  ) => unknown;
}

const StyledDiv = styled.div`
  padding: ${SPACERS.extraLarge};

  .update-indicator-forecast {
    .forecast-heading,
    .forecast-subheading {
      text-align: center;
    }
    .input-container {
      input {
        background-color: ${COLORS.whiteBase};
      }
    }
  }

  .update-indicator-measurement {
    .guess-heading {
      max-width: 600px;
    }
    .guess-heading,
    .selection-heading {
      text-align: center;
    }
    .selection {
      background-color: ${COLORS.whiteBase};
    }
  }

  .update-indicator-file {
    .file-heading {
      text-align: center;
    }
    .indicator-upload-file-container {
      justify-content: center;
      textarea {
        border-radius: ${SPACERS.extraTiny};
        border: 1px solid ${COLORS.grayBase};
        background-color: ${COLORS.whiteBase};
      }
    }
  }
`;

const indicators = {
  capacityDevelopment: [],
  act: [],
  learn: [],
  reach: [],
  succeed: [],
  catalyzed: [],
};

const defaultPeriod: ReportPeriod = {
  card: undefined,
  startDate: undefined,
  endDate: undefined,
};

const noOp = () => null;
const ImpactDetailsEditComponent: React.FC<ImpactDetailsEditComponentProps> = ({
  item,
  itemKey,
  reportId,
  sectionKey,
  questionRef,
}) => {
  const indicator = {} as Indicator;
  const allIndicators = {};
  switch (itemKey) {
    case 'forecast':
      return (
        <IndicatorForecast
          ref={questionRef}
          allIndicators={allIndicators}
          reportPeriod={defaultPeriod}
          isReportPassed={true}
          hideBottomButtons={true}
          requestState={'init'}
          reportId={reportId}
          indicatorDetails={item}
          indicators={indicators}
          setIndicators={noOp}
          selectedSectionKey={sectionKey}
          setSelectedSectionKey={noOp}
          selectedIndex={0}
          setSelectedIndex={noOp}
          onNext={noOp}
          onPrevious={noOp}
          className="update-indicator-forecast"
          indicator={indicator}
          guidanceText={undefined}
          saveRequestState={requestInit()}
          setRoute={noOp}
          handleIndicatorSave={noOp}
        />
      );
    case 'measurement':
      return (
        <IndicatorMeasurement
          ref={questionRef}
          allIndicators={allIndicators}
          isReportPassed={true}
          hideBottomButtons={true}
          requestState={'init'}
          reportId={reportId}
          reportPeriod={defaultPeriod}
          indicatorDetails={item}
          indicators={indicators}
          setIndicators={noOp}
          selectedSectionKey={sectionKey}
          setSelectedSectionKey={noOp}
          selectedIndex={0}
          setSelectedIndex={noOp}
          onNext={noOp}
          onPrevious={noOp}
          className="update-indicator-measurement"
          indicator={indicator}
          guidanceText={undefined}
          saveRequestState={requestInit()}
          setRoute={noOp}
          handleIndicatorSave={noOp}
        />
      );

    case 'file':
      return (
        <IndicatorFileUpload
          ref={questionRef}
          allIndicators={allIndicators}
          hideBottomButtons={true}
          requestState={'init'}
          reportId={reportId}
          isReportPassed={true}
          indicatorDetails={item}
          reportPeriod={defaultPeriod}
          indicators={indicators}
          setIndicators={noOp}
          selectedSectionKey={sectionKey}
          setSelectedSectionKey={noOp}
          selectedIndex={0}
          setSelectedIndex={noOp}
          onNext={noOp}
          onPrevious={noOp}
          className="update-indicator-file"
          indicator={indicator}
          guidanceText={undefined}
          saveRequestState={requestInit()}
          setRoute={noOp}
          handleIndicatorSave={noOp}
        />
      );

    default:
      return <>Not yet implemented</>;
  }
};

const ImpactDetailsEdit: React.FC<ImpactDetailsEditProps> = ({
  open,
  sectionKey,
  item,
  itemKey,
  reportId,
  handleCloseModal,
  handleUpdate,
  handleVerified,
}) => {
  const [saveReportIndicators, saveRequestState] = useSaveReportIndicators();
  const [errorText, setErrorText] = useState<string | undefined>();
  const ref = React.useRef<IndicatorDetailsHandler>(null);

  useEffect(() => {
    switch (saveRequestState.type) {
      case 'REQUEST_SUCCESS':
        handleVerified(sectionKey, item, itemKey);
        handleCloseModal();
        break;
      case 'REQUEST_ERROR':
        setErrorText('Unable to save the changes. Please try again!');
        break;
      default:
        break;
    }
  }, [saveRequestState.type, handleVerified, setErrorText]);

  const handleSave = useCallback(
    (
      indicator: IndicatorDetails,
      itemKey: keyof IndicatorDetails,
      answer: IndicatorDetails[keyof IndicatorDetails],
    ) => {
      (indicator[itemKey] as typeof answer) = answer;
      saveReportIndicators({
        reportId,
        file: indicator.file || undefined,
        forecast: parseInt(
          (indicator.forecast || '').toString().replace(/,/g, ''),
        ),
        finalForecast: parseInt(
          (indicator.finalForecast || '').toString().replace(/,/g, ''),
        ),
        interventionDosageLevel: indicator.interventionDosageLevel,
        interventionDosageType: indicator.interventionDosageType,
        interventionDosageNumber: indicator.interventionDosageNumber,
        timePeriod: indicator.timePeriod,
        indicatorId: indicator.indicatorId,
        measurement: indicator.measurement,
        happensAfterProgramYear: indicator.happensAfterProgramYear,
      });
    },
    [reportId, saveReportIndicators],
  );

  const handleDone = useCallback(() => {
    if (!ref.current) return;
    const { answer, errorText } = ref.current.answer?.();
    if (errorText) {
      setErrorText(errorText);
      return;
    }
    setErrorText(undefined);
    handleSave(item, itemKey, answer);
    handleUpdate(sectionKey, item, itemKey, answer);
  }, [sectionKey, item, itemKey, handleUpdate, setErrorText, handleSave]);

  const next: ButtonProps = {
    text: 'Save & Continue',
    onClick: handleDone,
    isLoading: false,
  };

  return (
    <TrueImpactModal
      open={open}
      maxWidth="1100px"
      errorText={errorText}
      onClose={handleCloseModal}
      next={next}
      previous={undefined}
      showExpandButton={true}
    >
      <StyledDiv>
        <ImpactDetailsEditComponent
          item={item}
          itemKey={itemKey}
          reportId={reportId}
          questionRef={ref}
          sectionKey={sectionKey}
        />
      </StyledDiv>
    </TrueImpactModal>
  );
};

export default ImpactDetailsEdit;
